import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorPanel } from './ErrorPanel';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Generic error boundary. Catches render errors in children and shows a fallback UI.
 *
 * Usage:
 *   <ErrorBoundary fallback={(error, reset) => <button onClick={reset}>Retry</button>}>
 *     <MyComponent />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === 'function') return fallback(error, this.reset);
    if (fallback) return fallback;

    return (
      <ErrorPanel
        title="This view hit a snag"
        message={error.message}
        onRetry={this.reset}
      />
    );
  }
}
